import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import { defaultMapProvider } from './mapProviderConfig';

function markerClass(state) {
  return `street-marker street-marker-${state}`;
}

function markerHtml(checkpoint, index) {
  const doneMark = checkpoint.state === 'done' ? '<span class="street-marker-check">✓</span>' : '';
  return `
    <button class="${markerClass(checkpoint.state)}" type="button" aria-label="${checkpoint.name}">
      ${doneMark}
      <span class="street-marker-dot"></span>
      <span class="street-marker-index">${index + 1}</span>
    </button>
  `;
}

function hasUsableRoute(journey) {
  return Array.isArray(journey.routePoints) && journey.routePoints.length > 0;
}

export default function StreetJourneyMap({
  journey,
  onSelectCheckpoint,
  provider = defaultMapProvider,
}) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [mapError, setMapError] = useState(false);

  useEffect(() => {
    if (!containerRef.current || !hasUsableRoute(journey)) return undefined;

    let map;
    try {
      map = L.map(containerRef.current, {
        attributionControl: true,
        scrollWheelZoom: false,
        zoomControl: false,
      });
      mapRef.current = map;

      L.tileLayer(provider.tileUrl, {
        attribution: provider.attribution,
        maxZoom: provider.maxZoom,
      }).addTo(map);

      const routeLine = L.polyline(journey.routePoints, {
        color: '#c8102e',
        weight: 4,
        opacity: 0.9,
      }).addTo(map);

      journey.checkpoints.forEach((checkpoint, index) => {
        if (checkpoint.lat === null || checkpoint.lng === null) return;

        const marker = L.marker([checkpoint.lat, checkpoint.lng], {
          icon: L.divIcon({
            className: 'street-marker-wrapper',
            html: markerHtml(checkpoint, index),
            iconAnchor: [14, 14],
          }),
          keyboard: true,
          title: checkpoint.name,
        }).addTo(map);

        marker.on('click', () => onSelectCheckpoint(checkpoint));
      });

      map.fitBounds(routeLine.getBounds(), {
        padding: provider.boundsPadding,
        maxZoom: 8,
      });
      setMapError(false);
    } catch (error) {
      setMapError(true);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [journey, onSelectCheckpoint, provider]);

  if (!hasUsableRoute(journey) || mapError) {
    return (
      <div className="street-map-fallback htv-card">
        <p className="htv-muted">Không tải được nền bản đồ. Vẫn có thể xem checkpoint bên dưới.</p>
        <div className="street-map-fallback-list">
          {journey.checkpoints.map((checkpoint) => (
            <button
              key={checkpoint.id}
              type="button"
              onClick={() => onSelectCheckpoint(checkpoint)}
            >
              <strong>{checkpoint.name}</strong>
              <span>{checkpoint.location}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="street-map-shell" aria-label="Bản đồ hành trình Việt Nam">
      <div ref={containerRef} className="street-map-canvas" />
    </div>
  );
}
